import React, { useState, useEffect } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator, Image, ImageStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, spacing, typography, chat } from '../../theme';
import { useAppContext } from '../../contexts/AppContext';
import { apiService } from '../../services/api';
import { getImageUrlSync } from '../../utils/imageProxy';
import { useLocale } from '../../contexts/LocaleContext';
import { createTranslator } from '../../i18n';
import { useThemedStyles } from '../../hooks/useThemedStyles';

export interface ProductLinkCardProps {
  productId: string;
  /** Route params from the CTA URL (itemId, businessId, contactId, phoneNumber, language) for opening product-view */
  routeParams?: Record<string, string>;
  bodyText?: string;
  isOutgoing?: boolean;
  /** When true, render as a special elevated card (outside bubble) */
  specialCard?: boolean;
}

export const ProductLinkCard: React.FC<ProductLinkCardProps> = ({
  productId,
  routeParams = {},
  bodyText,
  isOutgoing = false,
  specialCard = false,
}) => {
  const styles = useThemedStyles(getStyles);
  const { navigateTo } = useAppContext();
  const { locale } = useLocale();
  const t = createTranslator(locale);
  const [product, setProduct] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!productId) {
        setLoading(false);
        return;
      }
      try {
        const res = await apiService.getProductById(productId);
        if (!cancelled && res?.data) {
          setProduct(res.data);
          const firstVariant = res.data?.variants?.[0];
          if (firstVariant?.id) setSelectedVariantId(firstVariant.id);
        }
      } catch (_) {
        if (!cancelled) setProduct(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [productId]);

  const handleViewProduct = () => {
    navigateTo('product-view', undefined, {
      ...routeParams,
      productId,
    });
  };

  const handleViewProductInApp = () => {
    navigateTo('product-detail', productId);
  };

  const cardStyles = specialCard ? styles.specialCard : (isOutgoing ? styles.cardOutgoing : styles.cardIncoming);
  const textStyles = specialCard ? styles.specialText : (isOutgoing ? styles.textOutgoing : styles.textIncoming);

  if (loading) {
    return (
      <View style={[styles.card, cardStyles]}>
        {specialCard && (
          <View style={styles.specialHeader}>
            <Ionicons name="pricetag-outline" size={20} color={colors.primary} />
            <Text style={styles.specialBadge}>{t('card.product')}</Text>
          </View>
        )}
        <ActivityIndicator size="small" color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.primary)} style={{ padding: spacing.lg }} />
      </View>
    );
  }

  if (!product) {
    return (
      <View style={[styles.card, cardStyles]}>
        {specialCard && (
          <View style={styles.specialHeader}>
            <Ionicons name="pricetag-outline" size={20} color={colors.primary} />
            <Text style={styles.specialBadge}>{t('card.product')}</Text>
          </View>
        )}
        {!specialCard && <Ionicons name="cube-outline" size={24} color={isOutgoing ? chat.out.accent : colors.primary} />}
        <Text style={[styles.title, textStyles]}>{t('card.product')}</Text>
        {!specialCard && bodyText ? <Text style={[styles.body, textStyles]} numberOfLines={2}>{bodyText}</Text> : null}
        <TouchableOpacity
          style={specialCard ? styles.specialPrimaryBtn : [styles.actionBtn, isOutgoing ? styles.actionBtnOutgoing : styles.actionBtnIncoming]}
          onPress={handleViewProduct}
        >
          <Ionicons name="open-outline" size={18} color={specialCard ? colors.white : (isOutgoing ? chat.out.accent : colors.white)} />
          <Text style={[styles.actionLabel, specialCard ? styles.specialPrimaryBtnText : { color: isOutgoing ? chat.out.accent : colors.white }]}>{t('card.view')}</Text>
        </TouchableOpacity>
      </View>
    );
  }

  const imageUrl = product.images?.[0]?.url;
  const displayPrice = product.salePrice != null && product.salePrice < (product.price ?? 0)
    ? product.salePrice
    : product.price;
  const hasDiscount = product.salePrice != null && product.salePrice < (product.price ?? 0);
  const variants = product.hasVariants && Array.isArray(product.variants) ? product.variants : [];
  const selectedVariant = variants.find((v: any) => v.id === selectedVariantId);
  const variantPrice = selectedVariant?.price != null ? selectedVariant.price : displayPrice;

  const imgSize = specialCard ? 96 : 72;
  return (
    <View style={[styles.card, cardStyles]}>
      {specialCard && (
        <View style={styles.specialHeader}>
          <Ionicons name="pricetag-outline" size={20} color={colors.primary} />
          <Text style={styles.specialBadge}>Product</Text>
        </View>
      )}
      <View style={[styles.row, specialCard && styles.specialRow]}>
        {imageUrl ? (
          <Image
            source={{ uri: getImageUrlSync(imageUrl) }}
            style={[styles.productImage as ImageStyle, specialCard && { width: imgSize, height: imgSize }]}
            resizeMode="cover"
          />
        ) : (
          <View style={[
            styles.imagePlaceholder,
            specialCard && { width: imgSize, height: imgSize },
            isOutgoing && !specialCard ? styles.imagePlaceholderOutgoing : styles.imagePlaceholderIncoming,
            specialCard && styles.specialImagePlaceholder,
          ]}>
            <Ionicons name="image-outline" size={specialCard ? 40 : 32} color={specialCard ? colors.textLight : (isOutgoing ? chat.out.accent : colors.primary)} />
          </View>
        )}
        <View style={styles.productInfo}>
          <Text style={[styles.productName, textStyles, specialCard && styles.specialProductName]} numberOfLines={2}>{product.name}</Text>
          <View style={styles.priceRow}>
            {hasDiscount && (
              <Text style={[styles.originalPrice, textStyles]}>{product.currency || ''} {product.price}</Text>
            )}
            <Text style={[styles.price, textStyles, specialCard && styles.specialPrice]}>
              {product.currency || ''} {variantPrice != null ? variantPrice : displayPrice}
            </Text>
            {hasDiscount && (
              <Text style={[styles.discountBadge, specialCard ? styles.specialDiscountBadge : (isOutgoing ? styles.discountOutgoing : styles.discountIncoming)]}>
                {t('card.sale')}
              </Text>
            )}
          </View>
          {variants.length > 0 && (
            <View style={styles.variantsRow}>
              {variants.slice(0, 4).map((v: any) => (
                <TouchableOpacity
                  key={v.id}
                  style={[
                    styles.variantChip,
                    specialCard && styles.specialVariantChip,
                    selectedVariantId === v.id ? (specialCard ? styles.specialVariantChipSelected : (isOutgoing ? styles.variantChipSelectedOutgoing : styles.variantChipSelectedIncoming)) : (specialCard ? styles.specialVariantChipDefault : (isOutgoing ? styles.variantChipOutgoing : styles.variantChipIncoming)),
                  ]}
                  onPress={() => setSelectedVariantId(v.id)}
                >
                  <Text style={[styles.variantChipText, textStyles]} numberOfLines={1}>{v.name}</Text>
                </TouchableOpacity>
              ))}
            </View>
          )}
        </View>
      </View>
      {!specialCard && bodyText ? (
        <Text style={[styles.body, textStyles]} numberOfLines={2}>{bodyText}</Text>
      ) : null}
      <View style={[styles.actions, specialCard && styles.specialActions]}>
        <TouchableOpacity
          style={specialCard ? styles.specialPrimaryBtn : [styles.actionBtn, isOutgoing ? styles.actionBtnOutgoing : styles.actionBtnIncoming]}
          onPress={handleViewProduct}
        >
          <Ionicons name="open-outline" size={specialCard ? 18 : 16} color={specialCard ? colors.white : (isOutgoing ? chat.out.accent : colors.white)} />
          <Text style={[styles.actionLabel, specialCard ? styles.specialPrimaryBtnText : { color: isOutgoing ? chat.out.accent : colors.white }]}>{t('card.viewAndOrder')}</Text>
        </TouchableOpacity>
        <TouchableOpacity
          style={specialCard ? styles.specialSecondaryBtn : [styles.actionBtn, isOutgoing ? styles.actionBtnOutgoing : styles.actionBtnIncoming]}
          onPress={handleViewProductInApp}
        >
          <Ionicons name="list-outline" size={specialCard ? 18 : 16} color={specialCard ? colors.primary : (isOutgoing ? chat.out.accent : colors.white)} />
          <Text style={[styles.actionLabel, specialCard ? styles.specialSecondaryBtnText : { color: isOutgoing ? chat.out.accent : colors.white }]}>{t('card.inProducts')}</Text>
        </TouchableOpacity>
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  card: {
    borderRadius: 20,
    padding: 14,
    maxWidth: '92%',
  },
  cardOutgoing: {
    backgroundColor: chat.out.bg,
  },
  cardIncoming: {
    backgroundColor: colors.background.secondary,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.border,
  },
  label: {
    fontSize: typography.fontSize.xs,
    opacity: 0.9,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: spacing.sm,
    marginBottom: spacing.sm,
  },
  productImage: {
    width: 72,
    height: 72,
    borderRadius: 8,
  },
  imagePlaceholder: {
    width: 72,
    height: 72,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  imagePlaceholderOutgoing: {
    backgroundColor: chat.out.chip,
  },
  imagePlaceholderIncoming: {
    backgroundColor: colors.border,
  },
  productInfo: {
    flex: 1,
    minWidth: 0,
  },
  productName: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
    marginBottom: spacing.xs,
  },
  title: {
    fontSize: typography.fontSize.base,
    fontFamily: typography.fontFamily.medium,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    flexWrap: 'wrap',
  },
  price: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  originalPrice: {
    fontSize: typography.fontSize.xs,
    textDecorationLine: 'line-through',
    opacity: 0.8,
  },
  discountBadge: {
    fontSize: 10,
    fontFamily: typography.fontFamily.semibold,
    paddingHorizontal: 6,
    paddingVertical: 2,
    borderRadius: 4,
  },
  discountOutgoing: {
    backgroundColor: chat.out.chip,
    color: chat.out.accent,
  },
  discountIncoming: {
    backgroundColor: colors.primary,
    color: colors.white,
  },
  variantsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginTop: spacing.xs,
  },
  variantChip: {
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 8,
  },
  variantChipOutgoing: {
    backgroundColor: chat.out.chip,
  },
  variantChipIncoming: {
    backgroundColor: colors.border,
  },
  variantChipSelectedOutgoing: {
    backgroundColor: chat.out.chip,
  },
  variantChipSelectedIncoming: {
    backgroundColor: colors.primary,
  },
  variantChipText: {
    fontSize: typography.fontSize.xs,
  },
  body: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.sm,
    opacity: 0.95,
  },
  textOutgoing: {
    color: chat.out.text,
  },
  textIncoming: {
    color: colors.text.primary,
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
  },
  actionBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
    paddingVertical: spacing.xs,
    paddingHorizontal: spacing.sm,
    borderRadius: 8,
  },
  actionBtnOutgoing: {
    backgroundColor: chat.out.chip,
  },
  actionBtnIncoming: {
    backgroundColor: colors.primary,
  },
  actionLabel: {
    fontSize: typography.fontSize.xs,
    fontFamily: typography.fontFamily.medium,
  },
  // Special card (elevated, outside bubble) — single card, no wrapper
  specialCard: {
    backgroundColor: colors.background.surface,
    borderWidth: 1,
    borderColor: colors.borderMedium,
    borderRadius: 14,
    padding: 0,
    overflow: 'hidden',
    maxWidth: '100%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.12,
    shadowRadius: 8,
    elevation: 4,
  },
  specialHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    backgroundColor: colors.background.secondary,
  },
  specialBadge: {
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
    color: colors.primary,
  },
  specialText: {
    color: colors.text.primary,
  },
  specialRow: {
    paddingHorizontal: spacing.md,
    paddingTop: spacing.md,
    marginBottom: spacing.sm,
  },
  specialImagePlaceholder: {
    backgroundColor: colors.background.secondary,
  },
  specialProductName: {
    fontSize: typography.fontSize.lg,
    fontFamily: typography.fontFamily.semibold,
  },
  specialPrice: {
    fontSize: typography.fontSize.base,
  },
  specialDiscountBadge: {
    backgroundColor: colors.primaryTransparent,
    color: colors.primary,
  },
  specialVariantChip: {
    borderWidth: 1,
    borderColor: colors.borderMedium,
  },
  specialVariantChipDefault: {
    backgroundColor: colors.background.secondary,
  },
  specialVariantChipSelected: {
    backgroundColor: colors.primaryTransparent,
    borderColor: colors.primary,
  },
  specialActions: {
    paddingHorizontal: spacing.md,
    paddingBottom: spacing.md,
    gap: spacing.sm,
    minWidth: 280,
    maxWidth: 340,
  },
  specialPrimaryBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    backgroundColor: colors.primary,
  },
  specialPrimaryBtnText: {
    color: colors.white,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.semibold,
  },
  specialSecondaryBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: colors.primary,
    backgroundColor: 'transparent',
  },
  specialSecondaryBtnText: {
    color: colors.primary,
    fontSize: typography.fontSize.sm,
    fontFamily: typography.fontFamily.medium,
  },
});
